/**
 * Story 29.3 (ruling O6) — the owner-only "Choose who gets notifications"
 * banner, under the credit banner on every MainLayout page.
 *
 * Shown only to owners (`isOwner()` — superuser folded in, matching who can
 * save a contact) while the harness reports `needsTenantContact`: no tenant
 * default contact, or that person is no longer eligible. It is COMPUTED, so it
 * shows for every tenant until an owner picks someone, and disappears as soon
 * as they do (the save invalidates the same query). Members and viewers never
 * see it and never send the request (the query is disabled for them).
 * Renders nothing while loading or on error. Roles only.
 */

import { Link } from 'react-router-dom';
import { BellAlertIcon } from '@heroicons/react/24/outline';
import { usePermissions } from '@/lib/hooks/usePermissions';
import { useNotificationSettings } from '@/lib/hooks/useNotificationSettings';

const NOTIFICATION_SETTINGS_ROUTE = '/settings/notifications';

export default function NotificationContactBanner() {
  const { isOwner } = usePermissions();
  const owner = isOwner();
  const { data, isError } = useNotificationSettings({ enabled: owner, skipErrorToast: true });

  if (!owner || isError || !data?.needsTenantContact) return null;

  return (
    <div
      role="status"
      data-testid="notification-contact-banner"
      className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-line bg-surface px-4 py-1 text-sm text-ink"
    >
      {/* Icon and sentence wrap as ONE group, so a narrow screen never strands
          the bell on a line of its own. The sentence covers both causes: no
          contact set, or a contact who can no longer receive notifications. */}
      <span className="inline-flex items-start gap-3 py-2">
        <BellAlertIcon className="h-5 w-5 shrink-0 text-ink-muted" aria-hidden="true" />
        <span>No one can receive notifications for your organisation yet.</span>
      </span>
      <Link
        to={NOTIFICATION_SETTINGS_ROUTE}
        className="link-standalone focus-ring inline-flex min-h-[44px] items-center font-medium"
      >
        Choose who gets notifications
      </Link>
    </div>
  );
}
